import React from 'react';
import { Camera, Shield, Wifi, Settings, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const AdminIntelbrasPricing = ({ pricing, onChange, onSave, saveLoading, saveMsg }) => {
  if (!pricing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#00A651] animate-spin" />
      </div>
    );
  }

  const updateField = (section, index, field, value) => {
    onChange(prev => {
      const next = { ...prev, [section]: [...prev[section]] };
      next[section][index] = { ...next[section][index], [field]: Number(value) || 0 };
      return next;
    });
  };

  const PriceCard = ({ item, section, index, extraContent }) => (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
      <p className="text-white font-medium text-sm mb-1">{item.label}</p>
      {extraContent}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Mínimo (R$)</label>
          <input type="number" value={item.priceMin} min={0}
            onChange={e => updateField(section, index, 'priceMin', e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00A651]" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Máximo (R$)</label>
          <input type="number" value={item.priceMax} min={0}
            onChange={e => updateField(section, index, 'priceMax', e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00A651]" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 overflow-y-auto flex-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Camera className="w-5 h-5 text-[#00A651]" /> Tabela de Preços — Simulador de Orçamento
        </h3>
        {saveMsg && (
          <span className={`text-sm font-medium px-3 py-1 rounded-lg ${saveMsg.startsWith('✅') ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
            {saveMsg}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-6">
        Edite os preços do simulador em <span className="text-[#00A651]">/orcamento-intelbras</span>.
      </p>

      <div className="space-y-8">
        {/* Configuração PIX / Parcelamento */}
        <section className="bg-slate-800/40 border border-[#00A651]/20 rounded-2xl p-5">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2 text-sm">
            <Settings className="w-4 h-4 text-[#00A651]" /> Configuração de Preços e Parcelamento
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'pix_discount_pct',    label: 'Desconto PIX / À Vista (%)', hint: 'Ex: 10% → 10% a menos no PIX',           suffix: '%', type: 'float', defaultVal: 10 },
              { key: 'installment_fee_pct', label: 'Taxa Parcelamento — Máquina (%)', hint: 'Ex: 18% → acrescido no cartão',     suffix: '%', type: 'float', defaultVal: 18 },
              { key: 'max_installments',    label: 'Máximo de Parcelas',          hint: 'Número máximo de parcelas no cartão', suffix: '',  type: 'int',   defaultVal: 12 },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-400 block mb-1.5">{f.label}</label>
                <div className="relative">
                  <input type="number" min={0} step={f.type === 'float' ? 0.5 : 1}
                    value={pricing[f.key] ?? f.defaultVal}
                    onChange={e => onChange(p => ({ ...p, [f.key]: f.type === 'float' ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || f.defaultVal }))}
                    className={`w-full px-3 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00A651] ${f.suffix ? 'pr-8' : ''}`} />
                  {f.suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{f.suffix}</span>}
                </div>
                <p className="text-[10px] text-slate-600 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>
          {/* Preview */}
          {(() => {
            const disc = pricing.pix_discount_pct ?? 10;
            const fee  = pricing.installment_fee_pct ?? 18;
            const parc = pricing.max_installments ?? 12;
            const pix  = 1000 * (1 - disc / 100);
            const inst = (1000 * (1 + fee / 100)) / parc;
            return (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <p className="text-[10px] text-slate-500 mb-2 uppercase tracking-wide">Preview — base R$ 1.000</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="text-slate-500 line-through">R$ 1.000,00</span>
                  <span className="text-[#00A651] font-bold">R$ {pix.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} à vista PIX</span>
                  <span className="text-slate-300">ou em até {parc}x R$ {inst.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} no cartão</span>
                </div>
              </div>
            );
          })()}
        </section>

        {/* Câmeras */}
        <section>
          <h4 className="flex items-center gap-2 text-white font-semibold mb-3 pb-2 border-b border-slate-700">
            <Camera className="w-4 h-4 text-[#00A651]" /> Câmeras de Segurança (CFTV)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(pricing.cameras || []).map((cam, i) => (
              <PriceCard key={cam.id} item={cam} section="cameras" index={i}
                extraContent={<p className="text-slate-500 text-xs">— {cam.sub}</p>} />
            ))}
          </div>
        </section>

        {/* Alarme */}
        <section>
          <h4 className="flex items-center gap-2 text-white font-semibold mb-3 pb-2 border-b border-slate-700">
            <Shield className="w-4 h-4 text-[#00A651]" /> Sistema de Alarme
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {(pricing.alarm_zones || []).map((zone, i) => (
              <PriceCard key={zone.id} item={zone} section="alarm_zones" index={i}
                extraContent={<p className="text-slate-500 text-xs">— {zone.sub}</p>} />
            ))}
          </div>
        </section>

        {/* Wi-Fi */}
        <section>
          <h4 className="flex items-center gap-2 text-white font-semibold mb-3 pb-2 border-b border-slate-700">
            <Wifi className="w-4 h-4 text-[#00A651]" /> Rede Wi-Fi Intelbras
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {(pricing.wifi_coverage || []).map((wifi, i) => (
              <PriceCard key={wifi.id} item={wifi} section="wifi_coverage" index={i}
                extraContent={<p className="text-slate-500 text-xs">{wifi.sub} · {wifi.aps} AP{wifi.aps > 1 ? 's' : ''}</p>} />
            ))}
          </div>
        </section>

        {/* Botão Salvar */}
        <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur-sm pt-4 pb-2 -mx-6 px-6 border-t border-slate-700">
          <Button onClick={onSave} disabled={saveLoading}
            className="bg-gradient-to-r from-[#00A651] to-emerald-500 hover:opacity-90 text-white font-bold px-8 py-3 flex items-center gap-2"
            data-testid="save-intelbras-pricing-btn">
            {saveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar Preços
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminIntelbrasPricing;
